import { Calendar, ExternalLink, MapPin, Users } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Card, CardContent } from "@/components/ui/card"
import { useInitials } from "@/hooks/use-initials"
import { countryLabel } from "@/lib/countries"
import type { CrewMembershipDetail } from "@/types/crew"

/**
 * OKS-26 — Creator profile card (UX Spec V2 §TikTok facts card).
 *
 * Read-only snapshot of the creator's TikTok facts captured at join.
 * No edit affordance ships in V1 — refresh slated for V2 (helper line
 * surfaces the explanation).
 *
 * Layout (UX Spec):
 *   - Avatar (16) + handle/name (mono + muted)
 *   - dl row with `Users` followers, `MapPin` location, `Calendar`
 *     "Joined {relative}" chip + external `tiktok_url` `↗` link
 *   - Bio paragraph (max-h-48 with overflow scroll, whitespace-pre-line)
 *   - "Snapshotted at join" helper (muted)
 *
 * Locked testids:
 *   - `creator-detail-profile`     — root
 *   - `creator-detail-followers`   — followers stat
 *   - `creator-detail-location`    — location stat
 *   - `creator-detail-bio`         — bio paragraph
 *   - `creator-detail-tiktok-link` — external `↗` link to tiktok.com/@handle
 */
interface CreatorProfileCardProps {
  membership: Pick<CrewMembershipDetail, "joined_at" | "creator">
}

export function CreatorProfileCard({ membership }: CreatorProfileCardProps) {
  const { t, i18n } = useTranslation()
  const initials = useInitials()
  const { creator } = membership
  const locale = i18n.language.startsWith("fr") ? "fr" : "en"

  const locationText = formatLocation(creator.location, t, locale)
  const followersText =
    creator.follower_count === null
      ? t("crew.detail.profile.followers_unknown")
      : new Intl.NumberFormat().format(creator.follower_count)
  const joinedDate = new Date(membership.joined_at)
  const joinedRelative = relativeWhen(joinedDate, t)

  return (
    <Card data-testid="creator-detail-profile">
      <CardContent className="flex flex-col gap-4 pt-6">
        <div className="flex items-start gap-4">
          <Avatar className="size-16 shrink-0">
            {creator.avatar_url && (
              <AvatarImage
                src={creator.avatar_url}
                alt={creator.display_name}
              />
            )}
            <AvatarFallback className="text-base">
              {initials(creator.display_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="truncate font-mono text-xl">
              @{creator.tiktok_handle}
            </h2>
            <p className="text-muted-foreground truncate text-sm">
              {creator.display_name}
            </p>
            {/* External link to tiktok.com — BE-supplied URL (FE never
                reconstructs from handle). `noopener,noreferrer` guards
                window-opener leaks. */}
            <a
              href={creator.tiktok_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="creator-detail-tiktok-link"
              className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-xs underline-offset-4 hover:underline"
            >
              <ExternalLink aria-hidden="true" className="size-3" />
              {t("crew.detail.profile.view_on_tiktok")}
            </a>
          </div>
        </div>

        <dl className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div
            data-testid="creator-detail-followers"
            className="inline-flex items-center gap-2"
          >
            <Users
              aria-label={t("crew.detail.profile.followers_aria")}
              className="size-4"
            />
            <dt className="sr-only">
              {t("crew.detail.profile.followers_aria")}
            </dt>
            <dd className="tabular-nums">{followersText}</dd>
          </div>
          <div
            data-testid="creator-detail-location"
            className="inline-flex items-center gap-2"
          >
            <MapPin
              aria-label={t("crew.detail.profile.location_aria")}
              className="size-4"
            />
            <dt className="sr-only">
              {t("crew.detail.profile.location_aria")}
            </dt>
            <dd className="inline-flex items-center gap-1.5">
              <span>{locationText}</span>
            </dd>
          </div>
          <div className="inline-flex items-center gap-2">
            <Calendar className="size-4" aria-hidden="true" />
            <dt className="sr-only">{t("crew.detail.profile.joined_label")}</dt>
            <dd>
              <time dateTime={membership.joined_at}>
                {t("crew.detail.profile.joined_on", { when: joinedRelative })}
              </time>
            </dd>
          </div>
        </dl>

        <p
          data-testid="creator-detail-bio"
          className="max-h-48 overflow-y-auto text-sm whitespace-pre-line"
        >
          {creator.bio?.trim()
            ? creator.bio
            : t("crew.detail.profile.bio_empty")}
        </p>
        <p className="text-muted-foreground text-xs">
          {t("crew.detail.profile.snapshotted_at_join")}
        </p>
      </CardContent>
    </Card>
  )
}

function formatLocation(
  location: {
    city: string | null
    country: string | null
  },
  t: ReturnType<typeof useTranslation>["t"],
  locale: "en" | "fr",
): string {
  // OKS-348 — Localized country label via `lib/countries.ts#countryLabel`.
  const { city, country } = location
  const countryName = country ? countryLabel(country, locale) : null
  if (city && countryName)
    return t("crew.detail.profile.location_format", {
      city,
      country: countryName,
    })
  if (countryName) return countryName
  if (city) return city
  return t("crew.detail.profile.location_unknown")
}

function relativeWhen(
  date: Date,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const ms = Date.now() - date.getTime()
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
  if (days < 7) return t("crew.detail.profile.joined_relative_d", { n: days })
  if (days < 60)
    return t("crew.detail.profile.joined_relative_w", {
      n: Math.floor(days / 7),
    })
  return t("crew.detail.profile.joined_relative_mo", {
    n: Math.floor(days / 30),
  })
}
